import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, ValidationError } from '@netfoundry-ui/shared/model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { likeContains } from './frontdoor-v3-execution.service';
import { checkFrontdoorBody } from './frontdoor-v3-validation';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/**
 * An IPsec tunnel as the v3 API returns it. Core owns the bridge VM, the strongSwan responder, the
 * pre-shared key and the edge router, so most of these fields are core reporting back.
 */
export interface IpsecTunnelV3 {
    [key: string]: any;

    id: string;
    frontdoorId?: string;
    name?: string;
    description?: string;
    /** Public IPv4 address of the customer's own IPsec peer. */
    customerPublicIp?: string;
    /** The subnets behind that peer this tunnel reaches. Each entry carries no host bits. */
    customerPeerCidrs?: string[];
    /**
     * Where core put the bridge. Core picks all three, so they are read and never written: moving a bridge is a new
     * tunnel with a new address.
     */
    provider?: string;
    region?: string;
    hostSize?: string;
    /** Id of the IpsecTunneler in core. Absent until core has answered the create. */
    coreTunnelerId?: string;
    /** The tunneler-enabled edge router core runs on the bridge. */
    edgeRouterId?: string;
    /**
     * Ziti id of the identity behind that edge router. A share's dial policy names it directly, so its absence stops a
     * tunnel being nameable.
     */
    edgeRouterZitiId?: string;
    /**
     * How far core has got: `NEW`, `PROVISIONING`, `PROVISIONED`, `ERROR`, `DELETING` or `DELETED`. Read it through
     * {@link ipsecTunnelStatus}.
     */
    status?: string;
    createdAt?: string;
    updatedAt?: string;
}

/**
 * A request to create an IPsec tunnel. There is no key field and no placement: core generates the key
 * and picks the provider, the region and the host size. The key comes back once as `preSharedKeyOnce`,
 * so a caller who loses it reads the configuration document instead.
 */
export interface CreateIpsecTunnelV3 {
    frontdoorId: string;
    name: string;
    customerPublicIp: string;
    customerPeerCidrs: string[];
    description?: string;
}

/**
 * A request to change an IPsec tunnel. Only the fields the body carries are applied: `description`
 * clears when sent as `null` and stands when left out, and a rename also renames the bridge's edge
 * router. A new peer address or peer CIDR set makes core renegotiate, which stops traffic until the
 * customer's device agrees again.
 */
export interface UpdateIpsecTunnelV3 {
    name?: string;
    customerPublicIp?: string;
    customerPeerCidrs?: string[];
    description?: string | null;
}

/** The create response, which is the one place the pre-shared key ever appears. */
export interface CreatedIpsecTunnelV3 extends IpsecTunnelV3 {
    /**
     * Shown once and stored nowhere. Withheld from a caller holding `read` but not `update`, as the configuration
     * document is the only way back to it.
     */
    preSharedKeyOnce?: string;
}

/** The longest name the service accepts, from ZitiUtil.MAX_IPSEC_TUNNEL_NAME_LENGTH. */
export const MAX_IPSEC_TUNNEL_NAME_LENGTH = 162;

/** One page of tunnels, plus the link that gets the next one. */
export interface IpsecTunnelPageV3 {
    tunnels: IpsecTunnelV3[];
    /** The service's own link to the next page, or '' at the end. Opaque — nothing here parses it. */
    nextHref: string;
    /** Total across all pages when the service reports one, otherwise null. */
    total: number | null;
}

export const DEFAULT_IPSEC_TUNNEL_PAGE_SIZE = 50;

/**
 * Enough tunnels to fill a picker in a single read. A share names one tunnel, so the form needs them all rather than a
 * page.
 */
export const IPSEC_TUNNEL_PICKER_PAGE_SIZE = 500;

/**
 * What a tunnel listing can be narrowed by at the service. Only what `FindIpsecTunnel` binds, so the API answers on
 * every field here.
 */
export interface IpsecTunnelListFilters {
    /** Matched as a case-insensitive contains, so this takes a search box's value rather than an exact name. */
    name?: string;
    /** The wire statuses to list. Several are read as "any of these". */
    statuses?: string[];
}

/** Adds the filters a listing was asked for. An absent or empty one is left off entirely. */
export function withIpsecTunnelFilters(params: HttpParams, filters: IpsecTunnelListFilters = {}): HttpParams {
    let withFilters = params;
    const name = (filters.name ?? '').trim();
    if (name.length > 0) {
        withFilters = withFilters.set('name', likeContains(name));
    }
    // Repeated rather than joined: the parameter binds a set, and a comma-joined value would read as
    // a single status nothing is in.
    for (const status of filters.statuses ?? []) {
        if (status) {
            withFilters = withFilters.append('status', status);
        }
    }
    return withFilters;
}

/**
 * How far a tunnel has been built. Six states come from the service; `unknown` is this end's word for a status that is
 * absent or unrecognised.
 */
export type IpsecTunnelStatus =
    | 'new'
    | 'provisioning'
    | 'provisioned'
    | 'error'
    | 'deleting'
    | 'deleted'
    | 'unknown';

/** How a status reads on screen: the badge tone that carries it, and the words in it. */
export interface IpsecTunnelStatusBadge {
    tone: 'ok' | 'warn' | 'err' | 'muted';
    label: string;
}

const IPSEC_TUNNEL_STATUS_BADGES: Record<IpsecTunnelStatus, IpsecTunnelStatusBadge> = {
    new: { tone: 'warn', label: 'New' },
    provisioning: { tone: 'warn', label: 'Provisioning' },
    provisioned: { tone: 'ok', label: 'Provisioned' },
    error: { tone: 'err', label: 'Error' },
    deleting: { tone: 'warn', label: 'Deleting' },
    deleted: { tone: 'muted', label: 'Deleted' },
    unknown: { tone: 'muted', label: 'Unknown' },
};

/** The service's own names for those states, in the spelling the wire carries them in. */
const IPSEC_TUNNEL_WIRE_STATUSES = new Map<string, IpsecTunnelStatus>([
    ['NEW', 'new'],
    ['PROVISIONING', 'provisioning'],
    ['PROVISIONED', 'provisioned'],
    ['ERROR', 'error'],
    ['DELETING', 'deleting'],
    ['DELETED', 'deleted'],
]);

/** The states a tunnel is still moving through, so a page showing one should come back for it. */
const IPSEC_TUNNEL_TRANSITIONING: IpsecTunnelStatus[] = ['new', 'provisioning', 'deleting'];

/**
 * Read the status off a tunnel. Anything unsent or unrecognised is `unknown`, so a state added at the far end is not
 * mistaken for one of the six.
 */
export function ipsecTunnelStatus(tunnel: IpsecTunnelV3 | undefined): IpsecTunnelStatus {
    return IPSEC_TUNNEL_WIRE_STATUSES.get((tunnel?.status ?? '').toUpperCase()) ?? 'unknown';
}

/** How to draw `status`. One place, because the listing and the drawer must not disagree. */
export function ipsecTunnelStatusBadge(status: IpsecTunnelStatus): IpsecTunnelStatusBadge {
    return IPSEC_TUNNEL_STATUS_BADGES[status] ?? IPSEC_TUNNEL_STATUS_BADGES.unknown;
}

/** Whether the tunnel has settled, either built or broken. A page polls while this is false. */
export function isIpsecTunnelSettled(tunnel: IpsecTunnelV3 | undefined): boolean {
    return !IPSEC_TUNNEL_TRANSITIONING.includes(ipsecTunnelStatus(tunnel));
}

/**
 * Whether a share may name this tunnel: core is done, so the edge router exists and a dial policy can
 * select it. The service decides on a field it does not publish, so this repeats the rule.
 */
export function isIpsecTunnelDialable(tunnel: IpsecTunnelV3 | undefined): boolean {
    return ipsecTunnelStatus(tunnel) === 'provisioned' && !!tunnel?.edgeRouterZitiId;
}

/**
 * Why a tunnel cannot be named by a share, in the words a disabled picker entry carries, or '' when it
 * can be. A provisioned tunnel with no edge router is core still finishing, and reads the same way.
 */
export function ipsecTunnelUnavailableReason(tunnel: IpsecTunnelV3 | undefined): string {
    if (isIpsecTunnelDialable(tunnel)) {
        return '';
    }
    switch (ipsecTunnelStatus(tunnel)) {
        case 'new':
        case 'provisioning':
            return 'Still being built';
        case 'provisioned':
            return 'Waiting for its edge router';
        case 'error':
            return 'Its build failed';
        case 'deleting':
            return 'Being deleted';
        case 'deleted':
            return 'Deleted';
        default:
            return 'Not ready';
    }
}

/**
 * Reads IPsec tunnels from the v3 frontdoor API. Every path is scoped by the frontdoor id, so the
 * caller chooses one first. **Every method here answers 404 on a network without the `IPSEC_TUNNELER`
 * extension**, reads included — ask `FrontdoorExtensionAvailabilityService` before offering any of this.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3IpsecTunnelService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /**
     * One page of `frontdoorId`'s IPsec tunnels, newest first unless `sort` says otherwise. The service pages by offset,
     * so `page` counts from 0 and the page's `total` counts every match. `filters` narrows the listing at the service.
     */
    public findIpsecTunnels(
        frontdoorId: string,
        sort = 'createdAt,DESC',
        size = DEFAULT_IPSEC_TUNNEL_PAGE_SIZE,
        filters: IpsecTunnelListFilters = {},
        page = 0
    ): Observable<IpsecTunnelPageV3> {
        const params = withIpsecTunnelFilters(
            new HttpParams().set('sort', sort).set('size', String(size)).set('page', String(page)),
            filters
        );
        return this.listTunnels(frontdoorId, params);
    }

    /**
     * Every tunnel a share could be pointed at, by name. Read whole rather than paged, so a picker does
     * not hide the tunnel its reader came for. Not narrowed to the dialable ones — one still being built
     * is shown disabled with the reason.
     */
    public findTunnelsForPicker(frontdoorId: string): Observable<IpsecTunnelPageV3> {
        const params = new HttpParams()
            .set('sort', 'name,ASC')
            .set('size', String(IPSEC_TUNNEL_PICKER_PAGE_SIZE));
        return this.listTunnels(frontdoorId, params);
    }

    /**
     * Adds an IPsec tunnel to `frontdoorId` and asks core to build it. The body repeats the id, because
     * the service rejects a body naming a different account. **The answer carries `preSharedKeyOnce` and
     * nothing else ever will**, returning as soon as core accepts with the tunnel in `PROVISIONING`.
     */
    public createIpsecTunnel(frontdoorId: string, tunnel: CreateIpsecTunnelV3): Observable<CreatedIpsecTunnelV3> {
        const body = { ...tunnel, frontdoorId };
        return this.http
            .post(`${this.apiUrl}/v3/${frontdoorId}/ipsec-tunnels`, body, { headers: this.headers() })
            .pipe(map((response: any) => response as CreatedIpsecTunnelV3));
    }

    /**
     * Whether {@link createIpsecTunnel} would be accepted, without creating anything. Sends the same verb,
     * path and body as the write, and is authorized identically, so a caller without create still gets a 403.
     */
    public validateCreateIpsecTunnel(
        frontdoorId: string,
        tunnel: CreateIpsecTunnelV3
    ): Observable<ValidationError[]> {
        const body = { ...tunnel, frontdoorId };
        return checkFrontdoorBody(this.http, `${this.apiUrl}/v3/${frontdoorId}/ipsec-tunnels`, body);
    }

    /**
     * Whether an update would be accepted, without changing anything. Checks with `PUT` where the write is
     * a `PATCH`, because the method picks the rules and a `PUT` checks the tunnel as it would end up. The
     * caller must send a complete body, or `name`, `customerPublicIp` and `customerPeerCidrs` read missing.
     */
    public validateUpdateIpsecTunnel(
        frontdoorId: string,
        tunnelId: string,
        update: UpdateIpsecTunnelV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(
            this.http,
            `${this.apiUrl}/v3/${frontdoorId}/ipsec-tunnels/${tunnelId}`,
            update,
            'PUT'
        );
    }

    /**
     * One IPsec tunnel of `frontdoorId`. A tunnel addressed under another account answers 404, so a stale link cannot
     * read across accounts.
     */
    public getIpsecTunnel(frontdoorId: string, tunnelId: string): Observable<IpsecTunnelV3> {
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/ipsec-tunnels/${tunnelId}`, { headers: this.headers() })
            .pipe(map((response: any) => response as IpsecTunnelV3));
    }

    /**
     * Applies `update` to one tunnel. `PATCH`, so only the fields the body carries are applied. A new peer
     * address or peer CIDR set renegotiates the tunnel and stops traffic, so a caller asks before sending either.
     */
    public updateIpsecTunnel(
        frontdoorId: string,
        tunnelId: string,
        update: UpdateIpsecTunnelV3
    ): Observable<IpsecTunnelV3> {
        return this.http
            .patch(`${this.apiUrl}/v3/${frontdoorId}/ipsec-tunnels/${tunnelId}`, update, { headers: this.headers() })
            .pipe(map((response: any) => response as IpsecTunnelV3));
    }

    /**
     * Takes the tunnel down. The answer comes as soon as it is marked `DELETING`, and core takes the bridge
     * down afterwards. The service clears the field on every share that named it rather than refusing, so
     * those shares keep working over mTLS and lose the tunnel as a way in.
     */
    public deleteIpsecTunnel(frontdoorId: string, tunnelId: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/v3/${frontdoorId}/ipsec-tunnels/${tunnelId}`, {
            headers: this.headers(),
        });
    }

    /**
     * The document the customer configures their own IPsec device from, as markdown. **It carries the
     * pre-shared key in plaintext**, so it takes update permission rather than read and a caller offers it
     * as a download. A tunnel core holds no document for answers 404.
     */
    public getIpsecTunnelConfiguration(frontdoorId: string, tunnelId: string): Observable<string> {
        return this.http.get(`${this.apiUrl}/v3/${frontdoorId}/ipsec-tunnels/${tunnelId}/configuration`, {
            headers: new HttpHeaders().set('Accept', 'text/markdown'),
            responseType: 'text',
        });
    }

    /**
     * The next page, by following the link the service returned. Taken verbatim, as the cursor inside only resumes
     * under the order that made it.
     */
    public followNext(nextHref: string): Observable<IpsecTunnelPageV3> {
        return this.http
            .get(nextHref, { headers: this.headers() })
            .pipe(map((response: any) => this.readPage(response)));
    }

    private listTunnels(frontdoorId: string, params: HttpParams): Observable<IpsecTunnelPageV3> {
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/ipsec-tunnels`, { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    /** Accepts the HAL collection, a bare array, or a Spring page, so the shape can settle later. */
    private readPage(response: any): IpsecTunnelPageV3 {
        if (Array.isArray(response)) {
            return { tunnels: response, nextHref: '', total: response.length };
        }
        const tunnels = this.readList(response);
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : null;
        return { tunnels, nextHref: response?._links?.next?.href ?? '', total };
    }

    private readList(response: any): IpsecTunnelV3[] {
        const embedded = response?._embedded;
        if (embedded) {
            const collection = embedded.ipsecTunnels ?? embedded.ipsecTunnelList ?? Object.values(embedded)[0];
            if (Array.isArray(collection)) {
                return collection;
            }
        }
        if (Array.isArray(response?.content)) {
            return response.content;
        }
        this.logger.error('findIpsecTunnels: unrecognised response shape', response);
        return [];
    }
}
